import { DynamicForm } from "@shared/components/DynamicForm";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Filter, RotateCcw } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useBillCycleList, usePremiseList } from "../../hooks";

// Define the BillFilters interface to match BillTable
interface BillFilters {
  page?: number;
  limit?: number;
  search_data?: string;
  status?: string[];
  premise?: string[];
  consumer_type?: string[];
  bill_run_id?: string[];
  created_on_start?: string;
  created_on_end?: string;
  due_date_start?: string;
  due_date_end?: string;
  consumption_min?: number;
  consumption_max?: number;
  amount_min?: number;
  amount_max?: number;
  outstanding_min?: number;
  outstanding_max?: number;
}

interface BillFilterFormProps {
  currentFilters: BillFilters; // Changed from 'filters'
  onApplyFilters: (filters: Partial<BillFilters>) => void; // Changed from 'onFiltersChange'
  onResetFilters: () => void; // Changed from 'onClearFilters'
  onSavePreset?: (presetName: string, filters: Partial<BillFilters>) => void;
  savedPresets?: Array<{ name: string; filters: Partial<BillFilters> }>;
  onLoadPreset?: (filters: Partial<BillFilters>) => void;
}

const BillFilterForm: React.FC<BillFilterFormProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  onSavePreset,
  savedPresets = [],
  onLoadPreset,
}) => {
  const currency = useCurrency();
  const { data: billCycleList } = useBillCycleList();
  const { data: premiseList } = usePremiseList();

  // Temporary state for staged filter changes - following ConsumerList FilterPanel pattern
  const [tempFilters, setTempFilters] = useState<Partial<BillFilters>>({
    status: currentFilters.status || [],
    premise: currentFilters.premise || [],
    consumer_type: currentFilters.consumer_type || [],
    bill_run_id: currentFilters.bill_run_id || [],
    created_on_start: currentFilters.created_on_start || "",
    created_on_end: currentFilters.created_on_end || "",
    due_date_start: currentFilters.due_date_start || "",
    due_date_end: currentFilters.due_date_end || "",
    consumption_min: currentFilters.consumption_min,
    consumption_max: currentFilters.consumption_max,
    amount_min: currentFilters.amount_min,
    amount_max: currentFilters.amount_max,
    outstanding_min: currentFilters.outstanding_min,
    outstanding_max: currentFilters.outstanding_max,
  });

  // Sync temp filters when current filters change (URL updates) - following ConsumerList pattern
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters.status || [],
      premise: currentFilters.premise || [],
      consumer_type: currentFilters.consumer_type || [],
      bill_run_id: currentFilters.bill_run_id || [],
      created_on_start: currentFilters.created_on_start || "",
      created_on_end: currentFilters.created_on_end || "",
      due_date_start: currentFilters.due_date_start || "",
      due_date_end: currentFilters.due_date_end || "",
      consumption_min: currentFilters.consumption_min,
      consumption_max: currentFilters.consumption_max,
      amount_min: currentFilters.amount_min,
      amount_max: currentFilters.amount_max,
      outstanding_min: currentFilters.outstanding_min,
      outstanding_max: currentFilters.outstanding_max,
    };

    setTempFilters(newTempFilters);

    // Update form values
    filterForm.reset({
      status: newTempFilters.status,
      premise: newTempFilters.premise,
      consumer_type: newTempFilters.consumer_type,
      bill_run_id: newTempFilters.bill_run_id,
      created_on_start: newTempFilters.created_on_start,
      created_on_end: newTempFilters.created_on_end,
      due_date_start: newTempFilters.due_date_start,
      due_date_end: newTempFilters.due_date_end,
      consumption_min: newTempFilters.consumption_min,
      consumption_max: newTempFilters.consumption_max,
      amount_min: newTempFilters.amount_min,
      amount_max: newTempFilters.amount_max,
      outstanding_min: newTempFilters.outstanding_min,
      outstanding_max: newTempFilters.outstanding_max,
    });
  }, [currentFilters]);

  const filterFields: FormField[] = [
    {
      name: "created_on_start",
      type: "datepicker",
      label: "Bill Generation Start Date",
      placeholder: "Select start date",
      required: false,
    },
    {
      name: "created_on_end",
      type: "datepicker",
      label: "Bill Generation End Date",
      placeholder: "Select end date",
      required: false,
    },
    {
      name: "due_date_start",
      type: "datepicker",
      label: "Due Date Start",
      placeholder: "Select start date",
      required: false,
    },
    {
      name: "due_date_end",
      type: "datepicker",
      label: "Due Date End",
      placeholder: "Select end date",
      required: false,
    },
    {
      name: "status",
      type: "multi-select",
      label: "Bill Status",
      placeholder: "Select status...",
      options: [
        { value: "0", label: "CREATED" },
        { value: "1", label: "APPROVED" },
        { value: "2", label: "CONNECTED" },
        { value: "3", label: "HOLD" },
        { value: "4", label: "REJECTED" },
        { value: "5", label: "PASS" },
        { value: "6", label: "FAIL" },
        { value: "7", label: "RE_RUN" },
        { value: "8", label: "PENDING" },
      ],
      searchable: false,
      clearable: true,
      maxDisplayItems: 2,
    },
    {
      name: "premise",
      type: "multi-select",
      label: "Premise Type",
      placeholder: "Select premise type...",
      options: premiseList ?? [],
    },
    {
      name: "consumer_type",
      type: "multi-select",
      label: "Consumer Type",
      placeholder: "Select consumer type...",
      options: [
        { value: "Domestic", label: "Domestic" },
        { value: "Commercial", label: "Commercial" },
        { value: "Industrial", label: "Industrial" },
      ],
    },
    {
      name: "bill_run_id",
      type: "multi-select",
      label: "Bill Cycle",
      placeholder: "Select bill cycle...",
      options: billCycleList?.map((cycle) => ({
        value: cycle.id,
        label: cycle.name,
      })),
    },
    {
      name: "consumption_min",
      type: "number",
      label: "Minimum Consumption (unit)",
      defaultValue: undefined,
    },
    {
      name: "consumption_max",
      type: "number",
      label: "Maximum Consumption (unit)",
      defaultValue: undefined,
      placeholder: "1000",
    },
    {
      name: "amount_min",
      type: "decimal",
      label: `Minimum Amount (${currency})`,
      defaultValue: undefined,
      placeholder: "0.00",
    },
    {
      name: "amount_max",
      type: "decimal",
      defaultValue: undefined,
      label: `Maximum Amount (${currency})`,
      placeholder: "100.00",
    },
    {
      name: "outstanding_min",
      type: "decimal",
      defaultValue: undefined,
      label: `Minimum Outstanding (${currency})`,
      placeholder: "0.00",
    },
    {
      name: "outstanding_max",
      type: "decimal",
      defaultValue: undefined,
      label: `Maximum Outstanding (${currency})`,
      placeholder: "1000.00",
    },
  ];

  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: tempFilters,
  });

  // Update form when tempFilters change - following ConsumerList pattern
  useEffect(() => {
    filterForm.reset(tempFilters);
  }, [tempFilters, filterForm]);

  // Apply filters handler - following ConsumerList pattern
  const handleSubmit = () => {
    const payload = filterForm.getValues();
    const touchedFields = filterForm.formState.touchedFields;

    const numericFilterFields = [
      "consumption_min",
      "consumption_max",
      "amount_min",
      "amount_max",
      "outstanding_min",
      "outstanding_max",
    ];

    const cleanedPayload = Object.entries(payload).reduce(
      (acc, [key, value]) => {
        // Skip empty values
        if (value === "" || value === null || value === undefined) {
          return acc;
        }

        if (Array.isArray(value) && value.length === 0) {
          return acc;
        }

        // Only skip 0 if field was never touched
        if (
          numericFilterFields.includes(key) &&
          value === 0 &&
          !touchedFields[key]
        ) {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {} as Partial<BillFilters>
    );

    onApplyFilters(cleanedPayload);
  };

  // Reset filters handler - following ConsumerList pattern
  const handleReset = () => {
    setTempFilters({
      status: [],
      premise: [],
      consumer_type: [],
      bill_run_id: [],
      created_on_start: "",
      created_on_end: "",
      due_date_start: "",
      due_date_end: "",
      consumption_min: undefined,
      consumption_max: undefined,
      amount_min: undefined,
      amount_max: undefined,
      outstanding_min: undefined,
      outstanding_max: undefined,
    });
    filterForm.reset();
    onResetFilters();
  };

  // Handle preset loading - following ConsumerList pattern
  const handleLoadPreset = (preset: {
    name: string;
    filters: Partial<BillFilters>;
  }) => {
    setTempFilters(preset.filters);
    filterForm.reset(preset.filters);

    if (onLoadPreset) {
      onLoadPreset(preset.filters);
    }
  };

  const renderCustomFooter = () => (
    <div className="flex flex-col gap-4">
      {/* Saved Presets Section */}
      {savedPresets.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Saved Presets
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {savedPresets.map((preset, index) => (
              <Button
                key={index}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleLoadPreset(preset)}
                className="justify-start text-left"
              >
                {preset.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 items-center border-gray-200 pt-4">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Clear All
          </Button>

          {/* Save Preset Button - Optional */}
          {onSavePreset && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const presetName = prompt("Enter preset name:");
                if (presetName && presetName.trim()) {
                  const currentValues = filterForm.getValues();
                  onSavePreset(presetName.trim(), currentValues);
                }
              }}
              className="flex items-center gap-2"
            >
              Save Preset
            </Button>
          )}
        </div>

        <Button
          size="sm"
          className="flex items-center gap-2"
          onClick={handleSubmit}
        >
          <Filter className="w-4 h-4" />
          Apply Filters
        </Button>
      </div>
    </div>
  );

  return (
    <DynamicForm
      fields={filterFields}
      form={filterForm}
      onSubmit={handleSubmit}
      onReset={handleReset}
      config={{
        layout: "vertical",
        spacing: "medium",
        showSubmitButton: false,
        showResetButton: false,
      }}
      gridColumns={4}
      renderCustomFooter={renderCustomFooter}
    />
  );
};

export default BillFilterForm;
